<template>
  <div class="report-summary-container notranslate">
    <!-- 표준 보고서 템플릿 -->
    <div v-if="reportData.isStandardReport">
      <!-- 전체 요약 섹션 -->
      <div v-if="reportData.overviewContent" class="overview-section">
        <div class="section-header">
          <h3 class="section-title">Summary</h3>
        </div>
        <div class="overview-card">
          <div class="overview-text">{{ reportData.overviewContent }}</div>
          <!-- 키워드 표시 -->
          <div v-if="reportData.keywords.length > 0" class="keywords-container">
            <h4 class="keywords-title">Keywords</h4>
            <div class="keywords-list">
              <span v-for="(keyword, index) in reportData.keywords" :key="index" class="keyword-tag">
                {{ keyword }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- 의제 섹션 -->
      <div v-if="reportData.agendaItems.length > 0" class="agenda-section">
        <div class="section-header">
          <h3 class="section-title">Agenda</h3>
        </div>
        <div class="agenda-card">
          <div v-for="(item, index) in reportData.agendaItems" :key="index" class="agenda-item">
            <h4 class="agenda-title">{{ item.title }}</h4>
            <div class="agenda-content">
              <ul>
                <li v-for="(content, idx) in item.content" :key="idx">{{ content }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- 발언 요약 섹션 -->
      <div v-if="reportData.speakerSummaries.length > 0" class="speakers-section">
        <div class="section-header">
          <h3 class="section-title">Participants' Key Remarks</h3>
        </div>
        <div class="speakers-card">
          <div v-for="(speaker, index) in reportData.speakerSummaries" :key="index" class="speaker-item">
            <h4 class="speaker-name">{{ speaker.name }}</h4>
            <p class="speaker-content">{{ speaker.content }}</p>
          </div>
        </div>
      </div>

      <!-- 실행 과제 섹션 -->
      <div v-if="reportData.actionItems.length > 0" class="action-section">
        <div class="section-header">
          <h3 class="section-title">To-do</h3>
        </div>
        <div class="action-card">
          <div v-for="(item, index) in reportData.actionItems" :key="index" class="action-item">
            <input
              v-if="item.isBullet"
              type="checkbox"
              class="action-checkbox"
              disabled
            >
            <span class="action-text">{{ item.text }}</span>
          </div>
        </div>
      </div>

      <!-- To-do 없음 표시 -->
      <div v-if="reportData.actionItems.length === 0 && reportData.hasActionSection" class="action-section">
        <div class="section-header">
          <h3 class="section-title">To-do</h3>
        </div>
        <div class="action-card">
          <p class="no-action-items">No specific To-do were identified.</p>
        </div>
      </div>

      <!-- AI Comment 통합 섹션 -->
      <div v-if="reportData.hasAICommentsSection" class="ai-comments-section">
        <div class="section-header">
          <h3 class="section-title">AI Comment</h3>
        </div>
        <p class="ai-notice">The following content is generated by AI analysis.</p>
        <div class="ai-comments-card">
          <!-- To-do Sub-section -->
          <div v-if="reportData.aiTodoComments.length > 0" class="ai-comment-subsection">
            <h4 class="subsection-title">To-do</h4>
            <div v-for="(comment, index) in reportData.aiTodoComments" :key="`todo-${index}`" class="ai-comment-item">
              <h5 class="comment-title">{{ comment.title }}</h5>
              <div class="comment-content">
                <p><strong></strong> {{ comment.analysis }}</p>
                <div v-if="comment.references.length > 0" class="references">
                  <div class="reference-list">
                    <a v-for="(ref, idx) in comment.references" :key="idx" :href="ref" target="_blank" rel="noopener noreferrer" class="reference-preview">
                      <div class="reference-preview-title">{{ getHostname(ref) }}</div>
                      <div class="reference-preview-url">{{ ref }}</div>
                    </a>
                  </div>
                </div>
                <p v-if="comment.recommendation"><strong>suggestion:</strong> {{ comment.recommendation }}</p>
              </div>
            </div>
          </div>

          <!-- Agenda Sub-section -->
          <div v-if="reportData.aiAgendaComments.length > 0" class="ai-comment-subsection">
            <h4 class="subsection-title">Agenda</h4>
            <div v-for="(comment, index) in reportData.aiAgendaComments" :key="`agenda-${index}`" class="ai-comment-item">
              <h5 class="comment-title">{{ comment.title }}</h5>
              <div class="comment-content">
                <p><strong></strong> {{ comment.analysis }}</p>
                <div v-if="comment.references.length > 0" class="references">
                  <div class="reference-list">
                    <a v-for="(ref, idx) in comment.references" :key="idx" :href="ref" target="_blank" rel="noopener noreferrer" class="reference-preview">
                      <div class="reference-preview-title">{{ getHostname(ref) }}</div>
                      <div class="reference-preview-url">{{ ref }}</div>
                    </a>
                  </div>
                </div>
                <p v-if="comment.recommendation"><strong>suggestion:</strong> {{ comment.recommendation }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 푸터 섹션 -->
      <div v-if="reportData.footerContent" class="footer-section">
        <p class="footer-text" v-html="reportData.footerContent"></p>
      </div>
    </div>
    
    <!-- 대체 보고서 템플릿 -->
    <div v-else class="alternative-report" v-html="renderedMarkdown">
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import MarkdownIt from 'markdown-it'
import { ReportParser, getHostname } from '@/utils/reportParser'

// Props 정의
const props = defineProps({
  markdownContent: {
    type: String,
    default: ''
  }
})

// 반응형 데이터
const reportData = ref({})
const renderedMarkdown = ref('')

// 인스턴스 생성
const md = new MarkdownIt()
const parser = new ReportParser()

// 마크다운 파싱 함수
function parseMarkdownContent(content) {
  if (!content) {
    reportData.value = parser.getEmptyResult()
    return
  }

  console.log('=== 새로운 파서로 마크다운 파싱 시작 ===')
  
  const result = parser.parse(content)
  
  if (result.isStandardReport) {
    reportData.value = result
    console.log('표준 보고서 파싱 완료:', {
      summary: !!result.overviewContent,
      keywords: result.keywords.length,
      agenda: result.agendaItems.length,
      speakers: result.speakerSummaries.length,
      actions: result.actionItems.length,
      aiComments: (result.aiTodoComments?.length || 0) + (result.aiAgendaComments?.length || 0)
    })
  } else {
    reportData.value = { isStandardReport: false }
    renderedMarkdown.value = md.render(result.renderedMarkdown)
    console.log('대체 보고서로 처리됨')
  }
}

// 마크다운 컨텐츠 변경 감지
watch(() => props.markdownContent, (newContent) => {
  parseMarkdownContent(newContent)
}, { immediate: true })
</script>

<style scoped>
/* 기존 스타일 유지 - 스타일 코드는 동일하므로 생략 */
.report-summary-container {
  max-width: 100%;
  margin: 0 auto;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* 대체 보고서 스타일 */
.alternative-report {
  padding: 1rem;
  line-height: 1.7;
  color: #374151;
}

.alternative-report :deep(h1) {
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e5e7eb;
}

.alternative-report :deep(h3) {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.alternative-report :deep(ul) {
  padding-left: 1.5rem;
  margin: 0;
}

.alternative-report :deep(li) {
  margin-bottom: 0.5rem;
}

.alternative-report :deep(hr) {
  border: 0;
  height: 1px;
  background-color: #e5e7eb;
  margin: 2rem 0;
}

/* 섹션 공통 스타일 */
.overview-section,
.action-section,
.agenda-section,
.speakers-section,
.ai-comments-section,
.footer-section {
  margin-bottom: 2.5rem;
}

.section-header {
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e5e7eb;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: -0.025em;
}

/* 전체 요약 카드 스타일 */
.overview-card {
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.overview-text {
  color: #374151;
  line-height: 1.7;
  font-size: 1rem;
  margin-bottom: 2rem;
}

.keywords-container {
  margin-top: 1.25rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
}

.keywords-title {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.keywords-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.keyword-tag {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 0.125rem;
  font-size: 0.875rem;
  font-weight: 500;
}

/* 의제 카드 스타일 */
.agenda-card {
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.agenda-item {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.agenda-item:last-child {
  margin-bottom: 0;
  border-bottom: none;
  padding-bottom: 0;
}

.agenda-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1rem;
}

.agenda-content {
  color: #374151;
  font-size: 0.95rem;
  line-height: 1.6;
}

.agenda-content ul {
  margin: 0;
  padding-left: 1.5rem;
  list-style-type: disc;
}

.agenda-content li {
  margin-bottom: 0.5rem;
}

.agenda-content li::marker {
  color: #6b7280;
}

/* 발언 요약 카드 스타일 */
.speakers-card {
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.speaker-item {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.speaker-item:last-child {
  margin-bottom: 0;
  border-bottom: none;
  padding-bottom: 0;
}

.speaker-name {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
}

.speaker-content {
  color: #374151;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
}

/* 실행 과제 카드 스타일 */
.action-card {
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  padding: 1.5rem 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.action-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 0.25rem;
  border-bottom: 1px solid #e5e7eb;
  background: none;
  border-radius: 0;
  border: none;
  margin-bottom: 0;
}

.action-item:first-child {
  padding-top: 0;
}

.action-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.action-checkbox {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #d1d5db;
  border-radius: 0.125rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.action-text {
  flex: 1;
  color: #374151;
  line-height: 1.6;
  font-size: 0.95rem;
}

.no-action-items {
  color: #6b7280;
  font-style: italic;
  text-align: center;
  margin: 0;
}

/* AI 코멘트 카드 스타일 */
.ai-comments-card {
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.ai-notice {
  color: #6b7280;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  font-style: italic;
}

.ai-comment-subsection + .ai-comment-subsection {
  margin-top: 2.5rem;
}

.subsection-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1.5rem;
}

.ai-comment-item {
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
  background-color: #f9fafb;
}

.ai-comment-item:last-child {
  margin-bottom: 0;
}

.comment-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1rem;
}

.comment-content {
  color: #374151;
  font-size: 0.95rem;
  line-height: 1.6;
}

.comment-content p {
  margin-bottom: 1rem;
}

.references {
  margin: 1rem 0;
}

.reference-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.reference-preview {
  display: block;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
  padding: 1rem;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.2s ease-in-out;
}

.reference-preview:hover {
  background-color: #f9fafb;
  border-color: #d1d5db;
}

.reference-preview-title {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
}

.reference-preview-url {
  font-size: 0.875rem;
  color: #6b7280;
  word-break: break-all;
  text-decoration: none;
}

/* 푸터 스타일 */
.footer-section {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
}

.footer-text {
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
  font-style: italic;
  margin: 0;
}

/* 반응형 디자인 */
@media (max-width: 768px) {
  .section-title {
    font-size: 1.25rem;
  }
  
  .overview-card,
  .action-card,
  .agenda-card,
  .speakers-card,
  .ai-comments-card {
    padding: 1.5rem;
  }
  
  .keywords-list {
    gap: 0.5rem;
  }
  
  .keyword-tag {
    font-size: 0.8rem;
    padding: 0.375rem 0.75rem;
  }
}
</style>
